import { ComponentRef, Injectable, InjectionToken, Injector, OnDestroy, TemplateRef, Type, inject } from "@angular/core";
import { defer, Observable, Subject } from "rxjs";
import { Dialog, DialogConfig } from "@angular/cdk/dialog";
import { startWith } from "rxjs/operators";
import { Overlay, PositionStrategy } from "@angular/cdk/overlay";
import { ComponentType } from "@angular/cdk/portal";
import { VdsDrawerRef } from "./drawer-ref";
import { VdsDrawerConfig } from "./drawer-config";
import { VDS_DRAWER_DATA, VDS_DRAWER_DEFAULT_OPTIONS, VDS_DRAWER_SCROLL_STRATEGY } from "./tokens";
import { VdsDrawerContainer } from "./drawer-container";

/**
 * Unique ID for the drawer if none provided by the user
 */
let uniqueId = 0;

@Injectable({ providedIn: "root" })
export class VdsDrawer implements OnDestroy {
    private _overlay = inject(Overlay);
    private _defaultOptions = inject<VdsDrawerConfig>(VDS_DRAWER_DEFAULT_OPTIONS, { optional: true });
    private _scrollStrategy = inject(VDS_DRAWER_SCROLL_STRATEGY);
    private _parentDrawer = inject(VdsDrawer, { optional: true, skipSelf: true });

    private readonly _openDrawersAtThisLevel: VdsDrawerRef<any>[] = [];
    private readonly _afterAllClosedAtThisLevel = new Subject<void>();
    private readonly _afterOpenedAtThisLevel = new Subject<VdsDrawerRef<any>>();
    protected _drawer: Dialog;
    protected drawerConfigClass = VdsDrawerConfig;

    private readonly _drawerRefConstructor: Type<VdsDrawerRef<any>>;
    private readonly _drawerContainerType: Type<VdsDrawerContainer>;
    private readonly _drawerDataToken: InjectionToken<any>;

    /** Keeps track of the currently-open drawers. */
    get openDrawers(): VdsDrawerRef<any>[] {
        return this._parentDrawer ? this._parentDrawer.openDrawers : this._openDrawersAtThisLevel;
    }

    /** Stream that emits when a drawer has been opened. */
    get afterOpened(): Subject<VdsDrawerRef<any>> {
        return this._parentDrawer ? this._parentDrawer.afterOpened : this._afterOpenedAtThisLevel;
    }

    private _getAfterAllClosed(): Subject<void> {
        const parent = this._parentDrawer;
        return parent ? parent._getAfterAllClosed() : this._afterAllClosedAtThisLevel;
    }

    readonly afterAllClosed: Observable<void> = defer(() =>
        this.openDrawers.length ? this._getAfterAllClosed() : this._getAfterAllClosed().pipe(startWith(undefined))
    ) as Observable<any>;

    constructor() {
        const injector = inject(Injector);

        this._drawer = injector.get(Dialog);

        this._drawerRefConstructor = VdsDrawerRef;
        this._drawerContainerType = VdsDrawerContainer;
        this._drawerDataToken = VDS_DRAWER_DATA;
    }

    /**
     * Opens a drawer dialog containing the given component.
     * @param component Type of the component to load into the drawer.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened drawer.
     */
    open<T, D = any, R = any>(component: ComponentType<T>, config?: VdsDrawerConfig<D>): VdsDrawerRef<T, R>;

    /**
     * Opens a modal drawer containing the given template.
     * @param template TemplateRef to instantiate as the drawer content.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened drawer.
     */
    open<T, D = any, R = any>(template: TemplateRef<T>, config?: VdsDrawerConfig<D>): VdsDrawerRef<T, R>;

    open<T, D = any, R = any>(component: ComponentType<T>, config?: VdsDrawerConfig<D>): VdsDrawerRef<T, R>;

    open<T, D = any, R = any>(template: ComponentType<T> | TemplateRef<T>, config?: VdsDrawerConfig<D>): VdsDrawerRef<T, R>;

    open<T, D = any, R = any>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, config?: VdsDrawerConfig<D>): VdsDrawerRef<T, R> {
        let drawerRef: VdsDrawerRef<T, R>;
        config = { ...(this._defaultOptions || new VdsDrawerConfig()), ...config };
        config.id = config.id || `vds-drawer-${uniqueId++}`;
        config.scrollStrategy = config.scrollStrategy || this._scrollStrategy();

        const cdkRef = this._drawer.open<R, D, T>(componentOrTemplateRef, {
            ...config,
            positionStrategy: this.getPositionStrategy(config),
            disableClose: true,
            closeOnDestroy: false,
            closeOnOverlayDetachments: false,
            container: {
                type: this._drawerContainerType,
                providers: () => [
                    { provide: VdsDrawerConfig, useValue: config },
                    { provide: DialogConfig, useValue: config }
                ]
            },
            templateContext: () => ({ dialogRef: drawerRef }),
            providers: (ref, cdkConfig, dialogContainer) => {
                drawerRef = new this._drawerRefConstructor(ref, config, dialogContainer);
                drawerRef.updatePosition(config?.position);
                return [
                    { provide: this._drawerContainerType, useValue: dialogContainer },
                    { provide: this._drawerDataToken, useValue: cdkConfig.data },
                    { provide: this._drawerRefConstructor, useValue: drawerRef }
                ];
            }
        });

        // This can't be assigned in the `providers` callback, because
        // the instance hasn't been assigned to the CDK ref yet.
        (drawerRef! as { componentRef: ComponentRef<T> }).componentRef = cdkRef.componentRef!;
        drawerRef!.componentInstance = cdkRef.componentInstance!;

        this.openDrawers.push(drawerRef!);
        this.afterOpened.next(drawerRef!);

        drawerRef!.afterClosed().subscribe(() => {
            const index = this.openDrawers.indexOf(drawerRef);

            if (index > -1) {
                this.openDrawers.splice(index, 1);

                if (!this.openDrawers.length) {
                    this._getAfterAllClosed().next();
                }
            }
        });

        return drawerRef!;
    }

    getPositionStrategy(config: VdsDrawerConfig): PositionStrategy {
        const positionStrategy = this._overlay.position().global();
        positionStrategy.centerVertically();

        if (config.position === "left") {
            positionStrategy.left("0");
        } else {
            positionStrategy.right("0");
        }

        return positionStrategy;
    }

    /**
     * Closes all of the currently-open drawers.
     */
    closeAll(): void {
        this._closeDrawers(this.openDrawers);
    }

    /**
     * Finds an open drawer by its id.
     * @param id ID to use when looking up the drawer.
     */
    getDrawerById(id: string): VdsDrawerRef<any> | undefined {
        return this.openDrawers.find((dialog) => dialog.id === id);
    }

    ngOnDestroy() {
        // Only close the drawers at this level on destroy
        // since the parent service may still be active.
        this._closeDrawers(this._openDrawersAtThisLevel);
        this._afterAllClosedAtThisLevel.complete();
        this._afterOpenedAtThisLevel.complete();
    }

    private _closeDrawers(drawers: VdsDrawerRef<any>[]) {
        let i = drawers.length;

        while (i--) {
            drawers[i].close();
        }
    }
}
